import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../utils/axios';
import TsdPageBar from '../components/tsd/TsdPageBar';
import TsdScanCapture from '../components/tsd/TsdScanCapture';
import { useNotify } from '../components/NotifyProvider';
import usePrinter from '../hooks/usePrinter';
import { PRINT_TIMEOUT_MESSAGE, TSD_PRINT_TIMEOUT_MS, createPrintActionKeys } from '../utils/printClient';
import PalletCloseDialog from '../components/fulfillment/PalletCloseDialog';
import ConfirmDialog from '../components/fulfillment/ConfirmDialog';
import FulfillmentProductThumb from '../components/fulfillment/FulfillmentProductThumb';
import ServiceLabelActions from '../components/fulfillment/ServiceLabelActions';
import LabelingStepCard, { LabelingStateChip } from '../components/fulfillment/LabelingStepCard';
import {
  LABELING_STEPS,
  NO_PRINTER_MESSAGE,
  SCAN_UNRECOGNIZED_MESSAGE,
  flushedIncludes,
  flushedNotice,
  fulfillmentApiBase,
  fulfillmentTsdPath,
  isCompositionAlertError,
  isDuringPickingMode,
  isRemainingLabelingOrder,
  isServiceConfirmCode,
  labelingErrorMessage,
  labelingJobPath,
  labelingJobsPath,
  labelingModeInfo,
  labelingOrderPath,
  labelingStepHint,
  applyLabelingScanResult,
  mergeLabelingJob,
  nextLabelingState,
  normalizeFulfillmentMp,
  normalizeLabelingJob,
  normalizeLabelingOrder,
  nextAwaitingVerifyState,
  resumeLabelingState,
  scanErrorFeedback,
  scanStepBody,
  serviceScanNeedsPrinter,
  sortLabelingOrdersByArticle,
  transferMoveErrors,
  withAccountBody,
  withAccountParams,
} from '../utils/fulfillmentApi.mjs';
import { readPalletPrintAfterClose } from './fulfillmentUi.mjs';
import { takeScan } from './pickingUi.mjs';

// Скан обновляет заказ точечно; полный GET — страховка от расхождения списка и счётчиков.
const DETAIL_REFRESH_MS = 45_000;

function errorText(error) {
  if (!error?.response && error?.code === 'ECONNABORTED') return PRINT_TIMEOUT_MESSAGE;
  if (!error?.response && error?.message === 'Network Error') {
    return 'Нет связи с сервером. Проверьте Wi-Fi и повторите';
  }
  const data = error?.response?.data;
  const base = labelingErrorMessage(data) || error?.message || 'Не удалось выполнить операцию';
  const departed = data?.details?.departed_orders;
  if (!Array.isArray(departed) || !departed.length) return base;
  const lines = departed.slice(0, 12).map((row) => {
    const id = row.posting_number ?? row.wb_order_id ?? row.yandex_order_id ?? '—';
    return `${id}${row.article ? ` · ${row.article}` : ''} — ${row.reason || 'выбыл'}`;
  });
  return `${base}\n${lines.join('\n')}`;
}

function errorCode(error) {
  return error?.response?.data?.code || null;
}

export default function FulfillmentLabelingJob() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const printer = usePrinter();
  const printerUid = printer.selectedPrinter?.printer_uid || '';
  const {
    success: notifySuccess,
    error: notifyError,
    info: notifyInfo,
    warning: notifyWarning,
  } = useNotify();

  const marketplace = normalizeFulfillmentMp(
    searchParams.get('mp') || searchParams.get('marketplace') || '',
  ) || 'wb';
  const accountId = searchParams.get('account_id') || searchParams.get('accountId') || '';
  const apiBase = fulfillmentApiBase(marketplace);
  const listPath = fulfillmentTsdPath('/fulfillment/labeling', { mp: marketplace, accountId });

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [current, setCurrent] = useState(null);
  const [step, setStep] = useState(LABELING_STEPS.IDLE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [printError, setPrintError] = useState(null);
  const [lastPalletId, setLastPalletId] = useState(null);
  const [palletDialog, setPalletDialog] = useState(null);
  const [palletBusy, setPalletBusy] = useState(false);
  const [cancelPalletTarget, setCancelPalletTarget] = useState(null);
  const [ordersFilter, setOrdersFilter] = useState('left');
  const [userBusy, setUserBusy] = useState(null);

  const busyRef = useRef(false);
  const lastScanRef = useRef(null);
  const printKeysRef = useRef(null);
  if (!printKeysRef.current) printKeysRef.current = createPrintActionKeys();
  const printKeys = printKeysRef.current;
  const stateRef = useRef({ current: null, step: LABELING_STEPS.IDLE });
  stateRef.current = { current, step };
  const labelingMode = job?.labeling_mode || null;
  const duringPicking = isDuringPickingMode(labelingMode);

  const body = useCallback(
    (extra = {}) => withAccountBody(marketplace, accountId || job?.account_id, extra),
    [marketplace, accountId, job?.account_id],
  );

  const leaveToList = useCallback((message) => {
    navigate(listPath, { replace: true, state: { notice: message || '', skipSessionRedirect: true } });
  }, [navigate, listPath]);

  const setIdle = () => {
    setCurrent(null);
    setStep(LABELING_STEPS.IDLE);
    setPrintError(null);
  };

  const refreshDetail = useCallback(async () => {
    if (!jobId) return;
    try {
      const { data } = await axios.get(labelingJobsPath(jobId), {
        params: withAccountParams(marketplace, accountId),
        silent: true,
      });
      const detail = normalizeLabelingJob(data);
      if (detail) setJob(detail);
    } catch {
      /* фоновое обновление — ошибки покажет следующее действие */
    }
  }, [jobId, marketplace, accountId]);

  const hasJob = Boolean(job);
  useEffect(() => {
    if (!hasJob) return undefined;
    const timer = setInterval(() => {
      if (busyRef.current || document.visibilityState === 'hidden') return;
      void refreshDetail();
    }, DETAIL_REFRESH_MS);
    return () => clearInterval(timer);
  }, [hasJob, refreshDetail]);

  useEffect(() => {
    let cancelled = false;
    setJob(null);
    setReadOnly(false);
    setCurrent(null);
    setStep(LABELING_STEPS.IDLE);
    setError('');
    setUserBusy(null);
    lastScanRef.current = null;
    printKeys.clear();
    if (!jobId) {
      leaveToList('Не указан номер задания');
      return undefined;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(
          labelingJobsPath(jobId, 'take'),
          withAccountBody(marketplace, accountId, {}),
          { silent: true },
        );
        if (cancelled) return;
        const detail = normalizeLabelingJob(data);
        setJob(detail);
        setLastPalletId(detail?.pallets_open?.[0]?.id ?? null);
        // Сценарий A: «в руках»; иначе напечатанный непроверенный заказ — сразу шаг verify.
        // В режиме during_picking «в руках» = проклеено при сборке, на палету кладёт скан этикетки.
        const resume = resumeLabelingState(detail);
        if (resume.current) {
          setCurrent(resume.current);
          setStep(resume.step);
        }
      } catch (requestError) {
        if (cancelled) return;
        const code = errorCode(requestError);
        const details = requestError?.response?.data?.details || {};
        const status = Number(requestError?.response?.status);
        if (code === 'labeling_user_busy') {
          setUserBusy(details);
        } else if (code === 'labeling_locked') {
          // Занято другим: супервайзеру сервер отдаёт labeling.read_only — открываем просмотр,
          // остальным — в список с сообщением.
          const lockedMessage = `Задание уже взял ${details.locked_by_name || 'другой сотрудник'}`;
          try {
            const { data } = await axios.get(labelingJobsPath(jobId), {
              params: withAccountParams(marketplace, accountId),
              silent: true,
            });
            if (cancelled) return;
            const detail = normalizeLabelingJob(data);
            if (detail?.labeling?.read_only === true) {
              setJob(detail);
              setReadOnly(true);
            } else {
              leaveToList(lockedMessage);
            }
          } catch {
            if (!cancelled) leaveToList(lockedMessage);
          }
        } else if (code === 'labeling_not_ready' || status === 404) {
          leaveToList(errorText(requestError));
        } else if (status === 403) {
          try {
            const { data } = await axios.get(labelingJobsPath(jobId), {
              params: withAccountParams(marketplace, accountId),
              silent: true,
            });
            if (cancelled) return;
            setJob(normalizeLabelingJob(data));
            setReadOnly(true);
          } catch (readError) {
            if (!cancelled) setError(errorText(readError));
          }
        } else {
          setError(errorText(requestError));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Повторный take только при смене задания/кабинета.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, marketplace, accountId]);

  const showFlushed = (flushed, { asError = false, extra = '' } = {}) => {
    const text = [extra, flushedNotice(flushed)].filter(Boolean).join('\n');
    if (!text) return;
    if (asError) notifyError(text);
    else notifySuccess(text);
  };

  const rememberPallet = (data) => {
    const flushed = Array.isArray(data?.flushed) ? data.flushed : [];
    const landed = data?.pallet?.id ?? flushed[flushed.length - 1]?.pallet?.id;
    if (landed != null) setLastPalletId(landed);
  };

  const handleError = (requestError) => {
    const data = requestError?.response?.data || {};
    const msg = errorText(requestError);
    const flushed = data.details?.flushed;
    if (Array.isArray(flushed) && flushed.length) {
      rememberPallet({ flushed });
      if (flushedIncludes(flushed, stateRef.current.current?.order_key)) setIdle();
    }
    if (data.code === 'labeling_not_owner' || data.code === 'labeling_locked') {
      leaveToList(`${msg}${flushed?.length ? `\n${flushedNotice(flushed)}` : ''}`);
      return;
    }
    if (isCompositionAlertError(data)) {
      // Состав изменился (Ozon/Yandex): служебный ШК отклонён — печать только кнопкой на шаге confirm.
      const key = data.details?.order_key;
      const known = key != null
        ? (job?.orders || []).find((order) => String(order.order_key) === String(key))
        : null;
      const base = known || stateRef.current.current;
      if (base) {
        setCurrent({ ...base, composition_alert: true });
        setStep(LABELING_STEPS.CONFIRM);
      }
      setPrintError(null);
      setError(msg);
      notifyError(msg);
      return;
    }
    if (data.code === 'already_labeled' && data.details?.pallet?.id != null) {
      rememberPallet({ pallet: data.details.pallet });
    }
    if (requestError?.response) {
      // «Уже на палете» / неизвестный ШК / не по шагу — только тост, текущий шаг не трогаем.
      const feedback = scanErrorFeedback(data);
      const text = [feedback.message, flushedNotice(flushed)].filter(Boolean).join('\n');
      if (feedback.severity === 'info') {
        notifyInfo(text);
        void refreshDetail();
        return;
      }
      if (feedback.severity === 'warning') {
        notifyWarning(text);
        return;
      }
      if (data.code === 'step_locked') {
        notifyError(text);
        return;
      }
      if (feedback.resetStep) setIdle();
    }
    setError(msg);
    notifyError([msg, flushedNotice(flushed)].filter(Boolean).join('\n'));
    void refreshDetail();
  };

  const openPalletDialog = (pallet, { step: dialogStep = 'confirm', code = '', autoPrint = false } = {}) => {
    if (!pallet?.id) return;
    setPalletDialog({ pallet, step: dialogStep, code, error: '' });
    if (autoPrint && printerUid) void printPallet(pallet);
  };

  const applyResult = (data, { scannedCode = '' } = {}) => {
    if (!data) return;
    const applied = applyLabelingScanResult(job, data);
    const needsRefresh = applied.refresh;
    setJob((prev) => applyLabelingScanResult(prev, data).job);
    rememberPallet(data);
    if (data.kind === 'pallet') {
      showFlushed(data.flushed);
      if (data.pallet) openPalletDialog(data.pallet, { step: 'verify', code: scannedCode });
      void refreshDetail();
      return;
    }
    let next = nextLabelingState(data, stateRef.current, { mode: labelingMode });
    // Режим B: этикетка любого напечатанного заказа принимается на verify — затем следующий непроверенный.
    const chained = duringPicking && data.kind === 'label' && next.step === LABELING_STEPS.IDLE
      ? nextAwaitingVerifyState(applied.job?.orders, { excludeKeys: [data.order?.order_key] })
      : null;
    if (chained?.current) next = chained;
    setCurrent(next.current);
    setStep(next.step);
    if (data.kind === 'order') setPrintError(null);
    if (data.kind === 'confirm') setPrintError(data.print_error || null);
    if (data.kind === 'label' && data.print_error) setPrintError(data.print_error);
    if (next.step === LABELING_STEPS.IDLE && data.kind === 'label') setPrintError(null);
    if (data.kind === 'unrecognized') {
      notifyWarning([SCAN_UNRECOGNIZED_MESSAGE, flushedNotice(data.flushed)].filter(Boolean).join('\n'));
    } else if (data.kind === 'notice') {
      const text = [data.notice, flushedNotice(data.flushed)].filter(Boolean).join('\n');
      if (text) notifyInfo(text);
    } else if (duringPicking && data.kind === 'label' && (next.step === LABELING_STEPS.IDLE || chained?.current)) {
      // Заказ лёг на палету: только тост, баннер не нужен.
      const text = flushedNotice(data.flushed) || data.notice || '';
      if (text) notifySuccess(text);
      setNotice('');
    } else {
      showFlushed(data.flushed);
      setNotice(data.notice || '');
    }
    if (data.print_error) notifyError(data.print_error.message || 'Не удалось отправить на печать');
    if (needsRefresh) void refreshDetail();
  };

  const runAction = async (request) => {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      const { data } = await request();
      return data;
    } catch (requestError) {
      handleError(requestError);
      return null;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const onScan = async (raw) => {
    const code = String(raw || '').trim();
    if (!code) return;
    if (palletDialog) {
      // Скан ШК на шаге «наклеен?» означает, что этикетка уже на палете.
      setPalletDialog((prev) => (prev ? { ...prev, step: 'verify', code, error: '' } : prev));
      return;
    }
    if (!job || readOnly) return;
    if (!takeScan(lastScanRef, code, { busy: busyRef.current })) return;
    const state = stateRef.current;
    if (isServiceConfirmCode(code) && serviceScanNeedsPrinter(state.step) && !printerUid) {
      setError(NO_PRINTER_MESSAGE);
      notifyError(NO_PRINTER_MESSAGE);
      return;
    }
    setNotice('');
    const data = await runAction(() => axios.post(
      labelingJobsPath(jobId, 'scan'),
      body({
        barcode: code,
        printer_uid: printerUid || undefined,
        ...scanStepBody(state),
      }),
      { silent: true, timeout: TSD_PRINT_TIMEOUT_MS },
    ));
    if (!data) return;
    printKeys.clear();
    applyResult(data, { scannedCode: code });
  };

  const confirmPrint = async () => {
    if (!current?.order_key || readOnly) return;
    if (!printerUid) {
      setError(NO_PRINTER_MESSAGE);
      notifyError(NO_PRINTER_MESSAGE);
      return;
    }
    const data = await runAction(() => printKeys.run('label-confirm', `${current.order_key}|${printerUid}`, (key) => axios.post(
      labelingOrderPath(jobId, current.order_key, 'confirm'),
      body({ printer_uid: printerUid }),
      { silent: true, timeout: TSD_PRINT_TIMEOUT_MS, headers: { 'Idempotency-Key': key } },
    )));
    if (data) applyResult({ kind: 'confirm', ...data });
  };

  const reprint = async () => {
    if (!current?.order_key || readOnly) return;
    if (!printerUid) {
      setError(NO_PRINTER_MESSAGE);
      notifyError(NO_PRINTER_MESSAGE);
      return;
    }
    const data = await runAction(() => printKeys.run('label-reprint', `${current.order_key}|${printerUid}`, (key) => axios.post(
      labelingOrderPath(jobId, current.order_key, 'reprint'),
      body({ printer_uid: printerUid }),
      { silent: true, timeout: TSD_PRINT_TIMEOUT_MS, headers: { 'Idempotency-Key': key } },
    )));
    if (!data) return;
    const order = normalizeLabelingOrder(data.order, marketplace);
    if (order) setCurrent(order);
    if (data.print_error) {
      setPrintError(data.print_error);
      notifyError(data.print_error.message || 'Не удалось отправить на печать');
    } else {
      setPrintError(null);
      notifySuccess('Этикетка отправлена на печать');
    }
  };

  const flush = async () => {
    if (readOnly) return;
    const data = await runAction(() => axios.post(labelingJobsPath(jobId, 'flush'), body({}), { silent: true }));
    if (!data) return;
    if (data.job) setJob((prev) => mergeLabelingJob(prev, data.job));
    rememberPallet(data);
    if (Array.isArray(data.flushed) && data.flushed.length) showFlushed(data.flushed);
    else notifySuccess('Нечего класть на палету');
    setIdle();
    void refreshDetail();
  };

  const release = async () => {
    if (readOnly) {
      leaveToList('');
      return;
    }
    const data = await runAction(() => axios.post(labelingJobsPath(jobId, 'release'), body({}), { silent: true }));
    if (!data) return;
    const flushedText = flushedNotice(data.flushed);
    leaveToList([`Вы вышли из задания ${job?.code || ''}`.trim(), flushedText].filter(Boolean).join('\n'));
  };

  const printPallet = async (pallet) => {
    const target = pallet || palletDialog?.pallet;
    if (!target?.id) return;
    if (!printerUid) {
      setPalletDialog((prev) => (prev ? { ...prev, error: NO_PRINTER_MESSAGE } : prev));
      notifyError(NO_PRINTER_MESSAGE);
      return;
    }
    setPalletBusy(true);
    try {
      await printKeys.run('pallet-print', `${target.id}|${printerUid}`, (key) => axios.post(
        `${apiBase}/pallets/${target.id}/print`,
        body({ printer_uid: printerUid }),
        { silent: true, timeout: TSD_PRINT_TIMEOUT_MS, headers: { 'Idempotency-Key': key } },
      ));
      notifySuccess('Печать этикетки палеты отправлена');
    } catch (requestError) {
      const msg = errorText(requestError);
      setPalletDialog((prev) => (prev ? { ...prev, error: msg } : prev));
      notifyError(msg);
    } finally {
      setPalletBusy(false);
    }
  };

  const closePallet = async (scanned) => {
    const target = palletDialog?.pallet;
    const code = String(scanned || palletDialog?.code || '').trim();
    if (!target?.id) return;
    if (!code) {
      setPalletDialog((prev) => (prev ? { ...prev, error: 'Сканируй ШК палеты' } : prev));
      return;
    }
    setPalletBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/pallets/${target.id}/close`,
        body({ scanned_barcode: code, code }),
        { silent: true },
      );
      const closed = data?.pallet || data || {};
      showFlushed(data?.flushed, { extra: `Палета ${closed.code || target.code || ''} закрыта` });
      if (stateRef.current.step === LABELING_STEPS.IN_HANDS) setIdle();
      setPalletDialog(null);
      if (String(lastPalletId) === String(target.id)) setLastPalletId(null);
      await refreshDetail();
      if (readPalletPrintAfterClose() && printerUid) await printPallet(closed.id ? closed : target);
    } catch (requestError) {
      const reqCode = errorCode(requestError);
      const msg = errorText(requestError);
      if (reqCode === 'labeling_not_owner' || reqCode === 'labeling_locked') {
        leaveToList(msg);
        return;
      }
      const soft = reqCode === 'pallet_barcode_mismatch' || reqCode === 'validation_error';
      setPalletDialog((prev) => (prev ? { ...prev, error: msg, code: soft ? '' : prev.code } : prev));
      notifyError(msg);
      if (!soft) void refreshDetail();
    } finally {
      setPalletBusy(false);
    }
  };

  const cancelPallet = async () => {
    const target = cancelPalletTarget;
    if (!target?.id) return;
    setPalletBusy(true);
    try {
      await axios.post(`${apiBase}/pallets/${target.id}/cancel`, body({}), { silent: true });
      notifySuccess(`Палета ${target.code || target.id} убрана`);
      if (String(lastPalletId) === String(target.id)) setLastPalletId(null);
      await refreshDetail();
    } catch (requestError) {
      notifyError(errorText(requestError));
    } finally {
      setPalletBusy(false);
      setCancelPalletTarget(null);
    }
  };

  const progress = job?.progress || {};
  const progressTotal = progress.total || progress.transferred || 0;
  const progressPercent = progressTotal ? Math.round(((progress.labeled || 0) / progressTotal) * 100) : 0;
  const palletsOpen = job?.pallets_open || [];
  const activePallet = palletsOpen.find((pallet) => String(pallet.id) === String(lastPalletId))
    || palletsOpen[0]
    || null;
  const otherPallets = palletsOpen.filter((pallet) => pallet !== activePallet);
  const moveErrors = transferMoveErrors(job?.transfers);
  const orders = useMemo(() => job?.orders || [], [job]);
  const remainingCount = useMemo(() => orders.filter(isRemainingLabelingOrder).length, [orders]);
  const visibleOrders = useMemo(
    () => sortLabelingOrdersByArticle(ordersFilter === 'left' ? orders.filter(isRemainingLabelingOrder) : orders),
    [orders, ordersFilter],
  );
  const awaitingOthers = orders.filter((order) => order.labeling_state === 'awaiting_verify'
    && order.order_key !== current?.order_key);
  let scanHelper = labelingStepHint(step, current, { mode: labelingMode, context: 'labeling' });
  if (palletDialog) scanHelper = 'Сканируй ШК палеты';
  else if (readOnly) scanHelper = 'Просмотр — сканирование недоступно';
  else if (busy) scanHelper = 'Обработка…';

  return (
    <>
      <TsdPageBar
        title={job?.code || 'Проклейка'}
        startAdornment={(
          <Tooltip title="К списку">
            <IconButton
              aria-label="К списку"
              onClick={() => navigate(listPath, { state: { skipSessionRedirect: true } })}
            >
              <BackIcon />
            </IconButton>
          </Tooltip>
        )}
      />
      <Stack spacing={1.5} sx={{ maxWidth: 720, mx: 'auto', pb: 2, width: '100%', minWidth: 0 }}>
        {loading && <LinearProgress />}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{error}</Typography>
          </Alert>
        )}
        {notice && <Alert severity="info" onClose={() => setNotice('')}>{notice}</Alert>}
        {readOnly && job && (
          <Alert severity="info">
            {job.labeling?.locked_by
              ? `Задание в работе у ${job.labeling.locked_by_name || 'другого сотрудника'} — режим просмотра`
              : 'Просмотр без взятия — сканирование и действия недоступны'}
          </Alert>
        )}
        {!readOnly && job && !printer.loading && !printerUid && (
          <Alert severity="warning">Принтер не выбран — выберите его в Настройки → Печать</Alert>
        )}
        {moveErrors.map((transfer) => (
          <Alert key={transfer.id} severity="warning">
            Партия №{transfer.seq_no} ждёт перемещения МойСклад — её заказы пока нельзя проклеивать.
            {transfer.ms_move_error ? ` ${transfer.ms_move_error}` : ''}
          </Alert>
        ))}

        {!loading && !job && !userBusy && (
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography>Не удалось открыть задание</Typography>
            <Button sx={{ mt: 1 }} startIcon={<BackIcon />} onClick={() => leaveToList('')}>
              К списку
            </Button>
          </Paper>
        )}

        {job && (
          <>
            <Box>
              <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 8, borderRadius: 4 }} />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, minWidth: 0 }}>
                <Typography variant="bodyMedium" sx={{ flex: 1, minWidth: 0 }}>
                  {duringPicking ? 'на палете' : 'проклеено'} {progress.labeled || 0}
                  {' '}/ передано {progress.transferred || 0} / всего {progress.total || 0}
                  {(progress.departed || 0) > 0 ? ` · выбыло ${progress.departed}` : ''}
                </Typography>
                {duringPicking && (
                  <Chip size="small" variant="outlined" color={labelingModeInfo(labelingMode).color} label={labelingModeInfo(labelingMode).label} />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 32, minWidth: 0 }}>
                {activePallet ? (
                  <>
                    <Typography variant="bodyMedium" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
                      Палета {activePallet.code || `#${activePallet.id}`} · {Number(activePallet.items_count) || 0} шт
                    </Typography>
                    {!readOnly && (Number(activePallet.items_count) > 0 ? (
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        disabled={busy || palletBusy}
                        onClick={() => openPalletDialog(activePallet, { autoPrint: true })}
                      >
                        Закрыть
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        disabled={busy || palletBusy}
                        onClick={() => setCancelPalletTarget(activePallet)}
                      >
                        Убрать
                      </Button>
                    ))}
                  </>
                ) : (
                  <Typography variant="bodySmall" color="text.secondary">Палета создастся автоматически</Typography>
                )}
              </Stack>
              {otherPallets.length > 0 && (
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="bodySmall" color="text.secondary">Ещё открыты:</Typography>
                  {otherPallets.map((pallet) => (
                    <Chip
                      key={pallet.id}
                      size="small"
                      variant="outlined"
                      label={`${pallet.code || `#${pallet.id}`} · ${Number(pallet.items_count) || 0} шт`}
                      onClick={() => setLastPalletId(pallet.id)}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" color="inherit" onClick={release} disabled={busy}>
                Выйти из задания
              </Button>
              {!readOnly && <ServiceLabelActions printerUid={printerUid} disabled={busy} />}
            </Stack>

            <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
              <TsdScanCapture
                disabled={readOnly || busy}
                onScan={onScan}
                helperText={scanHelper}
                dialogPlaceholder="Штрихкод"
              />
              {!current && awaitingOthers.length > 0 && (
                <Typography variant="bodySmall" color="text.secondary">
                  Напечатано, но не проверено: {awaitingOthers.length}. Отсканируй товар или его этикетку.
                </Typography>
              )}
            </Paper>

            <LabelingStepCard
              current={current}
              step={step}
              marketplace={marketplace}
              labelingMode={labelingMode}
              busy={busy}
              readOnly={readOnly}
              printError={printError}
              onConfirm={confirmPrint}
              onReprint={reprint}
              onCancel={setIdle}
              onFlush={duringPicking ? null : flush}
            />

            <Paper variant="outlined">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, py: 1 }}>
                <Typography variant="titleSmall" sx={{ flex: 1 }}>Заказы задания</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={ordersFilter}
                  onChange={(_event, value) => { if (value) setOrdersFilter(value); }}
                >
                  <ToggleButton value="left" sx={{ px: 1.5 }}>Осталось {remainingCount}</ToggleButton>
                  <ToggleButton value="all" sx={{ px: 1.5 }}>Все {orders.length}</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <List dense disablePadding sx={{ borderTop: 1, borderColor: 'divider' }}>
                {visibleOrders.map((order) => {
                  const isCurrent = current?.order_key != null && order.order_key === current.order_key;
                  const extra = [
                    order.pallet ? order.pallet.code || `#${order.pallet.id}` : null,
                    order.departed?.reason || null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <ListItem
                      key={order.order_key}
                      divider
                      sx={{ gap: 1, alignItems: 'center', px: 1.5, py: 0.5, bgcolor: isCurrent ? 'action.selected' : undefined }}
                    >
                      <FulfillmentProductThumb task={order} marketplace={marketplace} size={36} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="bodyMedium" noWrap>
                          <Box component="span" sx={{ fontWeight: 600 }}>{order.article || 'Товар'}</Box>
                          {order.name && order.name !== order.article ? (
                            <Box component="span" sx={{ color: 'text.secondary' }}>{` · ${order.name}`}</Box>
                          ) : null}
                        </Typography>
                        <Typography
                          variant="bodySmall"
                          noWrap
                          sx={{ fontFamily: 'monospace', fontSize: '0.85rem', letterSpacing: 0.3 }}
                        >
                          {order.label_number_display || '—'}
                          {extra ? (
                            <Box component="span" sx={{ fontFamily: (theme) => theme.typography.fontFamily, color: 'text.secondary' }}>{` · ${extra}`}</Box>
                          ) : null}
                        </Typography>
                      </Box>
                      <LabelingStateChip state={order.labeling_state} mode={labelingMode} />
                    </ListItem>
                  );
                })}
                {!visibleOrders.length && (
                  <ListItem>
                    <Typography variant="bodySmall" color="text.secondary">
                      {ordersFilter === 'left' ? 'Всё проклеено' : 'Заказов нет'}
                    </Typography>
                  </ListItem>
                )}
              </List>
            </Paper>
          </>
        )}
      </Stack>

      <PalletCloseDialog
        open={Boolean(palletDialog)}
        pallet={palletDialog?.pallet}
        step={palletDialog?.step || 'confirm'}
        onStepChange={(next) => setPalletDialog((prev) => (prev ? { ...prev, step: next } : prev))}
        busy={palletBusy}
        error={palletDialog?.error || ''}
        onClearError={() => setPalletDialog((prev) => (prev ? { ...prev, error: '' } : prev))}
        externalScanMode
        scannedCode={palletDialog?.code || ''}
        onScannedCodeChange={(value) => setPalletDialog((prev) => (prev ? { ...prev, code: value } : prev))}
        onReprint={() => printPallet()}
        canPrint={Boolean(printerUid)}
        onCloseConfirm={closePallet}
        onCancel={() => { if (!palletBusy) setPalletDialog(null); }}
      />

      <ConfirmDialog
        open={Boolean(cancelPalletTarget)}
        title={`Убрать палету ${cancelPalletTarget?.code || ''}?`}
        message="Палета пустая. Она исчезнет из работы — в погрузку её добавить будет нельзя. Действие необратимо."
        confirmText="Убрать палету"
        loading={palletBusy}
        onClose={() => setCancelPalletTarget(null)}
        onConfirm={cancelPallet}
      />

      <Dialog open={Boolean(userBusy)} fullWidth maxWidth="xs">
        <DialogTitle>Уже есть задание в работе</DialogTitle>
        <DialogContent>
          <Typography>
            У вас уже взято задание {userBusy?.code || ''}. Сначала завершите его или выйдите из него.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => leaveToList('')} size="large" sx={{ minHeight: 52, flex: 1 }}>
            К списку
          </Button>
          <Button
            variant="contained"
            size="large"
            sx={{ minHeight: 52, flex: 1 }}
            disabled={userBusy?.job_id == null}
            onClick={() => navigate(labelingJobPath(userBusy, accountId), { replace: true })}
          >
            Перейти
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
